import clsx from 'clsx'
import NextImage from 'next/legacy/image'
import React, { useCallback, useEffect, useState } from 'react'
import Sticky from 'react-sticky-el'

import { useTranslate } from '../../contexts/TranslationContext'
import type { ProductDetailResult } from '../../data/ProductFragment'
import type { ProductPackageResult } from '../../data/ProductPackage'
import { useIntersectionObserver } from '../../utils/useIntersectionObserver'
import { Container } from '../shared/Container'
import { HeurekaButton } from '../shared/HeurekaButton'
import { Hidden } from '../shared/Hidden'
import { Rating } from '../shared/Rating'
import style from './ProductIntroSummary.module.sass'

type UseState<Value> = readonly [Value, (value: Value) => void]

type ProductIntroSummaryStickyProps = {
	package: ProductPackageResult | undefined
	product: ProductDetailResult
	state: UseState<null | HTMLDivElement>
}

export const ProductIntroSummarySticky = React.memo(function ProductIntroSummarySticky(
	props: ProductIntroSummaryStickyProps,
) {
	const [isHeaderHidden, setIsHeaderHidden] = useState(true)

	useIntersectionObserver(
		props.state[0],
		useCallback((entry) => setIsHeaderHidden(entry.isIntersecting), []),
	)

	const alternativeText = props.product.productLine?.name
		? `${props.product.brand?.name} ${props.product.productLine?.name} ${props.product.name}`
		: `${props.product.brand?.name} ${props.product.name}`

	const [scrollDir, setScrollDir] = useState('down')

	useEffect(() => {
		const threshold = 0
		let lastScrollY = window.pageYOffset
		let ticking = false

		const updateScrollDir = () => {
			const scrollY = window.pageYOffset

			if (Math.abs(scrollY - lastScrollY) < threshold) {
				ticking = false
				return
			}
			setScrollDir(scrollY > lastScrollY ? 'down' : 'up')
			lastScrollY = scrollY > 0 ? scrollY : 0
			ticking = false
		}

		const onScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(updateScrollDir)
				ticking = true
			}
		}

		window.addEventListener('scroll', onScroll)

		return () => window.removeEventListener('scroll', onScroll)
	}, [scrollDir])

	const translate = useTranslate()
	return (
		<div className={clsx(style.stickyHeader, isHeaderHidden && style.stickyHeaderHidden)}>
			<div className={style.stickyHead}>
				<Sticky stickyClassName={style.stickyWrapper}>
					<div
						className={clsx(
							style.sticky,
							isHeaderHidden ? style.stickyHidden : style.stickyVisible,
							scrollDir === 'up' && style.stickyHidden,
						)}
					>
						<Container size="wide" disableGutters>
							<div className={style.stickyBody}>
								<div className={style.stickyTitleWrapper}>
									<Hidden breakpoint="mobileDown">
										<div className={style.stickyImage}>
											<NextImage
												src={
													props.product.heurekaImage?.url ??
													props.product.image?.url ??
													'/images/placeholder-product.png'
												}
												title={alternativeText}
												alt={translate('alternativeText.productImage')}
												layout="fill"
												objectFit="contain"
											/>
										</div>
									</Hidden>
									<span className={style.stickyTitle}>{alternativeText}</span>
								</div>
								<Hidden breakpoint="mobileDown">
									<Rating rateAmount={props.product.score} isPlacedOnProductDetail />
								</Hidden>
								<div className={style.stickyButton}>
									<HeurekaButton id={props.package?.positionId} link={props.package?.link} />
								</div>
							</div>
						</Container>
					</div>
				</Sticky>
			</div>
		</div>
	)
})
